import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface AnimatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  variant?: "scale" | "slide-left" | "fade-up";
  closeOnBackdrop?: boolean;
  lockScroll?: boolean;
  backdropClass?: string;
}

const ENTER_MAP: Record<string, string> = {
  scale: "animate-scale-in",
  "slide-left": "animate-slide-in-left",
  "fade-up": "animate-fade-in-up",
};

const EXIT_MAP: Record<string, string> = {
  scale: "animate-scale-out",
  "slide-left": "animate-slide-out-left",
  "fade-up": "animate-fade-out-up",
};

export default function AnimatedModal({
  isOpen,
  onClose,
  children,
  variant = "scale",
  closeOnBackdrop = true,
  lockScroll = true,
  backdropClass = "",
}: AnimatedModalProps) {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const bodyLockedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const exitEndCalledRef = useRef(false);
  const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Lock / unlock body scroll ──
  const lockBody = useCallback(() => {
    if (!lockScroll) return;
    if (bodyLockedRef.current) return;
    document.body.style.overflow = "hidden";
    bodyLockedRef.current = true;
  }, [lockScroll]);

  const unlockBody = useCallback(() => {
    if (!bodyLockedRef.current) return;
    document.body.style.overflow = "";
    bodyLockedRef.current = false;
  }, []);

  // ── After exit animation ends: unmount + call onClose ──
  const handleExitEnd = useCallback(() => {
    if (exitEndCalledRef.current) return;
    exitEndCalledRef.current = true;
    setMounted(false);
    setClosing(false);
    unlockBody();
    setTimeout(() => {
      onCloseRef.current();
    }, 0);
  }, [unlockBody]);

  // ── Open ──
  useEffect(() => {
    if (isOpen) {
      exitEndCalledRef.current = false;
      setMounted(true);
      setClosing(false);
    }
  }, [isOpen]);

  // ── Lock body when mounted & not closing ──
  useEffect(() => {
    if (mounted && !closing) {
      const t = setTimeout(lockBody, 50);
      return () => clearTimeout(t);
    }
    unlockBody();
    return unlockBody;
  }, [mounted, closing, lockBody, unlockBody]);

  // ── Handle external close (isOpen became false) ──
  useEffect(() => {
    if (!isOpen && mounted && !closing) {
      setClosing(true);
      // Fallback: force unmount after 500ms if onAnimationEnd doesn't fire
      closingTimerRef.current = setTimeout(() => {
        handleExitEnd();
      }, 500);
    }
    return () => {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = null;
      }
    };
  }, [isOpen, mounted, closing, handleExitEnd]);

  // ── Backdrop click ──
  const handleBackdropClick = useCallback(() => {
    if (!closeOnBackdrop || closing) return;
    setClosing(true);
  }, [closeOnBackdrop, closing]);

  // ── Prevent content click from bubbling to backdrop ──
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // ── SAFETY NET: always unlock body on unmount ──
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
      bodyLockedRef.current = false;
    };
  }, []);

  if (!mounted) return null;

  const animClass = closing ? EXIT_MAP[variant] : ENTER_MAP[variant];

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-md ${
          closing ? "animate-fade-out" : "animate-fade-in"
        } ${backdropClass}`}
        onClick={handleBackdropClick}
        onAnimationEnd={(e) => {
          if (closing && e.target === e.currentTarget) {
            handleExitEnd();
          }
        }}
      />

      {/* Content */}
      <div
        className={`relative z-10 max-h-[90vh] overflow-y-auto ${animClass}`}
        onClick={handleContentClick}
        onAnimationEnd={(e) => {
          if (closing && e.target === e.currentTarget) {
            handleExitEnd();
          }
        }}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}